import { MAX_ALLOWED_EMAIL_DOMAINS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { ListEditor, type ListEditorLabels, type ListEntryResult } from "@sagot/ui";
import { normalizeEmailDomain } from "../settings-model";

const K = "settings.security";

interface AllowedEmailDomainsProps {
  domains: readonly string[];
  onChange: (domains: string[]) => void;
  disabled: boolean;
}

/** Settings → Security "Allowed email domains": one validated, lower-cased domain per entry (API: ≤ 20). */
export function AllowedEmailDomains({ domains, onChange, disabled }: AllowedEmailDomainsProps) {
  const { t } = useTranslation();
  const labels: ListEditorLabels = {
    add: t(`${K}.domainsAdd`),
    placeholder: t(`${K}.domainsPlaceholder`),
    empty: t(`${K}.domainsEmpty`),
    count: (count, max) => t(`${K}.domainsCount`, { count, max }),
    remove: (domain) => t(`${K}.domainsRemove`, { domain }),
    duplicate: (domain) => t(`${K}.domainsDuplicate`, { domain }),
    full: t(`${K}.domainsFull`, { max: MAX_ALLOWED_EMAIL_DOMAINS }),
    added: (domain) => t(`${K}.domainsAdded`, { domain }),
    removed: (domain) => t(`${K}.domainsRemoved`, { domain }),
  };
  const parse = (raw: string): ListEntryResult => {
    const value = normalizeEmailDomain(raw);
    return value ? { ok: true, value } : { ok: false, error: t(`${K}.domainsInvalid`) };
  };
  return (
    <ListEditor
      label={t(`${K}.domains`)}
      hint={t(`${K}.domainsHint`)}
      items={domains}
      onChange={onChange}
      parse={parse}
      max={MAX_ALLOWED_EMAIL_DOMAINS}
      labels={labels}
      icon="mail"
      disabled={disabled}
    />
  );
}
